import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Modal from './Modal';
import f from './forms.module.css';
import s from './GiveModal.module.css';
import { TAX_DISCLOSURE, SUPPORT_EMAIL } from '../config';
import { cadencePhrase, formatDate } from '../lib/format';

const PRESETS = [20, 50, 100, 500, 1000];
const MAX_COUNT = { week: 52, month: 12, year: 1 };

const isoPlusDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const MIN_START = isoPlusDays(2);

const clampCount = (unit, n) => Math.min(Math.max(parseInt(n, 10) || 1, 1), MAX_COUNT[unit]);

// Map preset cadence keys to a Stripe interval/count pair.
const PRESET_INTERVAL = {
  weekly: { interval: 'week', count: 1 },
  monthly: { interval: 'month', count: 1 },
  annually: { interval: 'year', count: 1 },
};

export default function GiveModal({ onClose }) {
  const [step, setStep] = useState(1);

  const [giftType, setGiftType] = useState('one_time');
  const [cadence, setCadence] = useState('monthly'); // weekly | monthly | annually | custom
  const [customUnit, setCustomUnit] = useState('week');
  const [customCount, setCustomCount] = useState(2);
  const [startMode, setStartMode] = useState('today'); // today | date
  const [startDate, setStartDate] = useState(MIN_START);

  const [preset, setPreset] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [designation, setDesignation] = useState('');
  const [designate, setDesignate] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const recurring = giftType === 'recurring';
  const isCustom = preset === null;
  const amount = isCustom ? Number(customAmount) : preset;

  const resolved = recurring
    ? (cadence === 'custom'
        ? { interval: customUnit, count: clampCount(customUnit, customCount) }
        : PRESET_INTERVAL[cadence])
    : null;
  const freqPhrase = resolved ? cadencePhrase(resolved.interval, resolved.count) : '';

  const chip = (active) => (active ? `${f.chip} ${f.chipActive}` : f.chip);

  const pickUnit = (u) => {
    setCustomUnit(u);
    setCustomCount((c) => clampCount(u, c));
  };

  const goNext = () => {
    setError('');
    if (recurring && startMode === 'date' && (!startDate || startDate < MIN_START)) {
      setError('Please choose a start date at least 2 days out.');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Please enter a valid email so we can send your receipt.');
      return;
    }
    if (!amount || amount < 1) {
      setError('Please choose or enter an amount of at least 1.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const payload = {
        gift_type: giftType,
        amount,
        designation: designation.trim() || null,
        email: email.trim(),
        phone: phone.trim() || null,
      };
      if (recurring) {
        payload.cadence = cadence;
        if (cadence === 'custom') {
          payload.custom_interval = customUnit;
          payload.custom_count = clampCount(customUnit, customCount);
        }
        payload.start_date = startMode === 'date' ? startDate : null;
      }

      const res = await fetch('/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Checkout could not be started');
      }
      const { url } = await res.json();
      if (!url) throw new Error('Checkout could not be started');
      window.location.href = url;
    } catch (err) {
      setError(err.message || `Something went wrong. Please try again, or email us at ${SUPPORT_EMAIL}.`);
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} label="Give to Revive Worship" size="md">
      <motion.div
        key={step}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {step === 1 ? (
          <>
            <span className={f.label}>give · step 1 of 2</span>
            <h2 className={f.title}>How would you like to <em>give?</em></h2>

            <div className={f.field}>
              <label className={f.fieldLabel}>gift type</label>
              <div className={f.chips}>
                <button type="button" className={chip(giftType === 'one_time')} onClick={() => setGiftType('one_time')}>one-time</button>
                <button type="button" className={chip(recurring)} onClick={() => setGiftType('recurring')}>recurring</button>
              </div>
            </div>

            {recurring && (
              <>
                <div className={f.field}>
                  <label className={f.fieldLabel}>frequency</label>
                  <div className={f.chips}>
                    <button type="button" className={chip(cadence === 'weekly')} onClick={() => setCadence('weekly')}>weekly</button>
                    <button type="button" className={chip(cadence === 'monthly')} onClick={() => setCadence('monthly')}>monthly</button>
                    <button type="button" className={chip(cadence === 'annually')} onClick={() => setCadence('annually')}>annually</button>
                    <button type="button" className={chip(cadence === 'custom')} onClick={() => setCadence('custom')}>custom</button>
                  </div>

                  {cadence === 'custom' && (
                    <div className={s.inlineRow}>
                      <span className={s.everyLabel}>every</span>
                      {customUnit !== 'year' && (
                        <input
                          className={`${f.input} ${s.countInput}`}
                          type="number"
                          min="1"
                          max={MAX_COUNT[customUnit]}
                          value={customCount}
                          onChange={(e) => setCustomCount(clampCount(customUnit, e.target.value))}
                          aria-label="repeat count"
                        />
                      )}
                      <div className={f.chips}>
                        <button type="button" className={chip(customUnit === 'week')} onClick={() => pickUnit('week')}>weeks</button>
                        <button type="button" className={chip(customUnit === 'month')} onClick={() => pickUnit('month')}>months</button>
                        <button type="button" className={chip(customUnit === 'year')} onClick={() => pickUnit('year')}>years</button>
                      </div>
                    </div>
                  )}
                  {cadence === 'custom' && <p className={f.helper}>Repeats {freqPhrase}.</p>}
                </div>

                <div className={f.field}>
                  <label className={f.fieldLabel}>starts</label>
                  <div className={f.chips}>
                    <button type="button" className={chip(startMode === 'today')} onClick={() => setStartMode('today')}>today</button>
                    <button type="button" className={chip(startMode === 'date')} onClick={() => setStartMode('date')}>on a date</button>
                  </div>
                  {startMode === 'date' && (
                    <input
                      className={`${f.input} ${s.dateInput}`}
                      type="date"
                      min={MIN_START}
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      aria-label="start date"
                    />
                  )}
                  {startMode === 'date' && <p className={f.helper}>Your card is saved now; the first gift is charged on your start date.</p>}
                </div>
              </>
            )}

            {error && <p className={f.errorMsg}>{error}</p>}

            <button type="button" className={f.primary} onClick={goNext}>
              continue <span aria-hidden="true">→</span>
            </button>
          </>
        ) : (
          <>
            <button type="button" className={s.back} onClick={() => { setError(''); setStep(1); }}>
              <span aria-hidden="true">←</span> how to give
            </button>
            <span className={f.label}>give · step 2 of 2</span>
            <h2 className={f.title}>Your <em>gift.</em></h2>

            <p className={s.recap}>
              {recurring
                ? <>Recurring · <strong>{freqPhrase}</strong> · {startMode === 'date' ? <>starts {formatDate(startDate)}</> : 'starts today'}</>
                : <>One-time gift</>}
            </p>

            <form onSubmit={handleSubmit}>
              <div className={f.field}>
                <label className={f.fieldLabel}>amount</label>
                <div className={f.chips}>
                  {PRESETS.map((p) => (
                    <button type="button" key={p} className={chip(preset === p)} onClick={() => { setPreset(p); setCustomAmount(''); }}>
                      ${p}
                    </button>
                  ))}
                  <button type="button" className={chip(isCustom)} onClick={() => setPreset(null)}>other</button>
                </div>
              </div>

              {isCustom && (
                <div className={f.field}>
                  <label className={f.fieldLabel}>custom amount</label>
                  <input
                    className={f.input}
                    type="number"
                    min="1"
                    step="1"
                    inputMode="decimal"
                    placeholder="enter an amount in USD"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              <p className={f.helper}>Shown in USD. Your local currency appears at secure checkout.</p>

              <div className={f.field}>
                <label className={f.fieldLabel}>email</label>
                <input className={f.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
              </div>

              <div className={f.field}>
                <label className={f.fieldLabel}>phone (optional)</label>
                <input className={f.input} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
                <p className={f.helper}>We'll text your donation receipts and a link to your giving record.</p>
              </div>

              <div className={f.field}>
                <label className={f.fieldLabel}>would you like this gift used in a specific way?</label>
                <div className={f.chips}>
                  <button type="button" className={chip(!designate)} onClick={() => { setDesignate(false); setDesignation(''); }}>no, where needed most</button>
                  <button type="button" className={chip(designate)} onClick={() => setDesignate(true)}>yes</button>
                </div>
                {designate && (
                  <input
                    className={`${f.input} ${s.revealInput}`}
                    type="text"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    maxLength={120}
                    autoFocus
                    aria-label="how you would like this gift used"
                  />
                )}
              </div>

              <p className={f.transparencyLine}>{TAX_DISCLOSURE}</p>

              {error && <p className={f.errorMsg}>{error}</p>}

              <button type="submit" className={f.primary} disabled={submitting}>
                {submitting ? 'redirecting…' : 'continue to checkout'}
                <span aria-hidden="true">→</span>
              </button>
            </form>
          </>
        )}
      </motion.div>
    </Modal>
  );
}
